/**
 * 姓名合法性校验方法
 * 支持中英文姓名、少数民族姓名校验
 * @param {string} name - 待校验的姓名
 * @returns {object} 校验结果对象
 */
export const validateName = (name) => {
    // 默认返回值
    const defaultResult = {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };

    const errorResult = (message, messageType = '', isHtml = false) => ({
        status: -1,
        message,
        messageType,
        isHtml
    });

    // 基本空值检查
    if (!name || typeof name !== 'string') {
        return errorResult('姓名不能为空');
    }

    const trimmedName = name.trim();

    // 长度检查
    if (trimmedName.length < 2) {
        return errorResult('姓名长度过短，至少需要2个字符');
    }

    if (trimmedName.length > 30) {
        return errorResult('姓名长度过长，不能超过30个字符');
    }

    // 去除首尾空格后的再次检查
    if (trimmedName.length === 0) {
        return errorResult('姓名不能为空或纯空格');
    }

    // 检查是否包含非法字符（数字、特殊符号等）
    const illegalCharRegex = /[0-9!@#$%^&*()_+=\[\]{};:"\\|,.<>?/~`]/;
    if (illegalCharRegex.test(trimmedName)) {
        return errorResult('姓名包含非法字符（数字或特殊符号）');
    }

    // 连续空格检查
    const consecutiveSpaceRegex = /\s{2,}/;
    if (consecutiveSpaceRegex.test(trimmedName)) {
        return errorResult('姓名中不能有连续空格');
    }

    // 根据姓名类型进行不同规则的校验
    const hasChinese = /[\u4e00-\u9fa5]/.test(trimmedName);
    const hasEnglish = /[a-zA-Z]/.test(trimmedName);

    // 混合语言检查（中英文混合）
    if (hasChinese && hasEnglish) {
        return errorResult('姓名不能同时包含中文和英文');
    }

    // 中文姓名校验
    if (hasChinese) {
        return validateChineseName(trimmedName, errorResult);
    }

    // 英文姓名校验
    if (hasEnglish) {
        return validateEnglishName(trimmedName, errorResult);
    }

    // 其他字符（可能是少数民族文字或纯空格等）
    return validateOtherName(trimmedName, errorResult);
};

/**
 * 中文姓名校验
 */
const validateChineseName = (name, errorResult) => {
    // 中文姓名正则：只包含中文汉字、少数民族点符（·）和空格
    const chineseNameRegex = /^[\u4e00-\u9fa5·\s]+$/;

    if (!chineseNameRegex.test(name)) {
        return errorResult('中文姓名只能包含汉字、间隔符（·）和空格');
    }

    // 去除所有空格和间隔符后的纯汉字长度
    const pureChinese = name.replace(/[·\s]/g, '');

    if (pureChinese.length < 2) {
        return errorResult('中文姓名至少需要2个汉字');
    }

    if (pureChinese.length > 15) {
        return errorResult('中文姓名汉字数量不能超过15个');
    }

    // 检查间隔符使用是否合理
    const dotCount = (name.match(/·/g) || []).length;
    if (dotCount > 2) {
        return errorResult('姓名中间隔符（·）不能超过2个');
    }

    // 检查间隔符位置（不能在开头、结尾或连续出现）
    if (name.startsWith('·') || name.endsWith('·') || name.includes('··')) {
        return errorResult('间隔符（·）使用位置不正确');
    }

    // 少数民族姓名特殊字符检查（扩展少数民族字符范围）
    const minorityChars = /^[\u4e00-\u9fa5\u3100-\u312f\u31a0-\u31bf·\s]+$/;
    if (!minorityChars.test(pureChinese)) {
        return errorResult('姓名包含不支持的字符');
    }

    return {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };
};

/**
 * 英文姓名校验
 */
const validateEnglishName = (name, errorResult) => {
    // 英文姓名正则：字母、空格、连字符、撇号
    const englishNameRegex = /^[a-zA-Z\s\-']+$/;

    if (!englishNameRegex.test(name)) {
        return errorResult('英文姓名只能包含字母、空格、连字符（-）和撇号（\'）');
    }

    // 分割名字各部分
    const nameParts = name.split(/\s+/).filter(part => part.length > 0);

    if (nameParts.length < 2) {
        return errorResult('英文姓名至少需要包含姓和名（用空格分隔）');
    }

    if (nameParts.length > 5) {
        return errorResult('英文姓名部分过多，不能超过5个部分');
    }

    // 检查每个部分的长度和格式
    for (let i = 0; i < nameParts.length; i++) {
        const part = nameParts[i];

        if (part.length < 2) {
            return errorResult(`姓名部分"${part}"过短，至少需要2个字母`);
        }

        if (part.length > 20) {
            return errorResult(`姓名部分"${part}"过长，不能超过20个字母`);
        }

        // 检查连字符使用
        if (part.startsWith('-') || part.endsWith('-')) {
            return errorResult(`"${part}"中连字符使用位置不正确`);
        }

        if (part.includes('--')) {
            return errorResult(`"${part}"中不能有连续连字符`);
        }

        // 检查撇号使用
        const apostropheCount = (part.match(/'/g) || []).length;
        if (apostropheCount > 1) {
            return errorResult(`"${part}"中撇号使用过多`);
        }

        if (part.startsWith("'") || part.endsWith("'")) {
            return errorResult(`"${part}"中撇号使用位置不正确`);
        }
    }

    // 首字母大写检查（严格模式下要求）
    for (let i = 0; i < nameParts.length; i++) {
        const part = nameParts[i];
        const subParts = part.split('-');

        for (let j = 0; j < subParts.length; j++) {
            const subPart = subParts[j];
            if (subPart.length > 0 && !/^[A-Z]/.test(subPart)) {
                return errorResult(
                    `"${subPart}"首字母需要大写`,
                    'dialog',
                    false
                );
            }
        }
    }

    return {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };
};

/**
 * 其他类型姓名校验（少数民族文字等）
 */
const validateOtherName = (name, errorResult) => {
    // 基本字符检查：字母、空格、常见标点
    const basicCharRegex = /^[a-zA-Z\s\-'·]+$/;

    if (!basicCharRegex.test(name)) {
        return errorResult(
            '姓名包含不支持的字符类型，请使用中文、英文或常见少数民族文字',
            'dialog',
            false
        );
    }

    // 基本长度检查
    const pureName = name.replace(/[·\s\-']/g, '');

    if (pureName.length < 2) {
        return errorResult('姓名有效字符过少，至少需要2个有效字符');
    }

    if (pureName.length > 25) {
        return errorResult('姓名有效字符过多，不能超过25个字符');
    }

    return {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };
};

export default {
    validateName
};
